/**
 * Theme — system / light / dark, persisted per device (D-25, AUD-5 I-6).
 *
 * `DESIGN.md:255` has asked for this since before the audit, and dark was already a real
 * theme: its text palette is the healthier of the two (AUD-7 §4.1) and the three genuinely
 * broken components in it were fixed in WS-K1. Shipping a toggle only makes choosable what
 * the product already renders.
 *
 * "system" is the default and it REMOVES the attribute rather than resolving it, so
 * `prefers-color-scheme` stays authoritative and a macOS auto-appearance user flips live at
 * sunset with no reload and no listener. Resolving "system" to a concrete value at boot would
 * freeze whatever it happened to be at sign-in.
 *
 * Same shape as the retired `stores/surface.ts`, deliberately: the boot wiring is a rename,
 * not a redesign.
 */
export type ThemeChoice = "system" | "light" | "dark";

const KEY = "genz.theme";
const CHOICES: ThemeChoice[] = ["system", "light", "dark"];

function isChoice(v: string | null): v is ThemeChoice {
  return v !== null && (CHOICES as string[]).includes(v);
}

/** The saved choice, or "system". Never throws — a locked-down localStorage is not an error. */
export function getTheme(): ThemeChoice {
  try {
    const raw = localStorage.getItem(KEY);
    return isChoice(raw) ? raw : "system";
  } catch {
    // Private mode, a storage quota, a hardened embed: the default is still a working app.
    return "system";
  }
}

/**
 * Put the choice on the document. "system" removes the attribute entirely — that is what
 * hands authority back to `prefers-color-scheme`.
 */
export function applyTheme(t: ThemeChoice): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (t === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", t);
}

/** Persist and apply. A failed write still applies: the session should honour the click. */
export function setTheme(t: ThemeChoice): void {
  try {
    localStorage.setItem(KEY, t);
  } catch {
    /* not persisted; still applied below */
  }
  applyTheme(t);
}
